<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { addDays, startOfDay, differenceInCalendarDays } from 'date-fns'
import { toZonedTime, fromZonedTime, format } from 'date-fns-tz'

import { useConfigStore } from '../../../../../stores/config'
import { usePageStore } from '../../../../../stores/page'
import { useEpgAirTimeHelper } from '../../../../../composables/use-epg-air-time-helper'
import { useMobileDialogBodyClass } from '../../../../../composables/use-mobile-dialog-body-class'
import { useEpgWidgetTracking } from '../../../../../composables/use-epg-widget-tracking'
import ApplyOrDiscardDialog from './ApplyOrDiscardDialog.vue'
import FilterHeader from './Header.vue'
import StaticHeader from './StaticHeader.vue'
import ThematicHeader from './ThematicHeader.vue'
import ChannelHeader from './ChannelHeader.vue'
import SelectedFilters from './Selected.vue'
import AirTimeDateHeading from './AirTimeDateHeading.vue'
import { useKeepTabbingFocus } from '../../../../../composables/use-keep-tabbing-focus'
import { isDesktopViewport } from '../../../../../utils/viewport'

import type { EpgWidgetParagraph } from '../../../../../utils/paragraph-schema/paragraphs'
import type { FilterState } from '../types'

type EpgWidget = EpgWidgetParagraph['epgWidget']
type Channels = NonNullable<EpgWidget['channels']>

// `airTimeOptions` isn't part of the composer-schema (it's a client-derived filter label), but some
// integrations still pass it through on `initialFilters` - kept optional here for backwards compatibility.
type InitialFilters = NonNullable<EpgWidget['initialFilters']> & { airTimeOptions?: string }
type Config = NonNullable<EpgWidget['config']>

type FilterTag = {
  key: string,
  value?: string,
  label: string
}

const TIME_CHIPS = ['20:15', '22:00']
// The always-visible quick links (mobile trigger + panel presets); a time matching one of these needs no own tag
const QUICK_TIME_OPTIONS = ['now', ...TIME_CHIPS]
const DAY_LABELS: Record<number, string> = { '-1': 'Gestern', 0: 'Heute', 1: 'Morgen' }
const WEEKDAY_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const WEEKDAY_LONG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']

const props = withDefaults(defineProps<{
  initialFilters?: InitialFilters,
  restoredFilters?: FilterState,
  viewMode?: string,
  config?: Config,
  channels?: Channels,
  isPreview?: boolean
}>(), {
  viewMode: 'overview'
})

const emit = defineEmits<{
  'update-filters': [filters: FilterState],
  'filters-ready': [filters: FilterState]
}>()

const { trackEventInDataLayer } = useEpgWidgetTracking()
// NOTE: currently unused (no focus-trap is ever activated on this container) but kept to preserve the exact
// behaviour of the pre-conversion component, which also spread its (equally unused) return value into `this`.
useKeepTabbingFocus()

const { timeZone, airTimeNow, getAirTimeByAirTimeOption, forceDateIntoTimeZone } = useEpgAirTimeHelper()
const configStore = useConfigStore()
const pageStore = usePageStore()

const showApplyOrDiscardDialog = ref(false)
const filtersReady = ref(false)
const filtersOpen = ref(false)
const filtersMemory = ref<string | null>(null)
const selectedFilters = ref<FilterState>({ categoryIds: [] })

const activeHeaderVariant = computed(() => {
  // channel detail pages get their own header regardless of `isThematic` - that flag is reserved for the
  // upcoming topic-page slider variant (separate session) and must not affect channel pages
  if (props.viewMode === 'channel') return 'channel'

  return props.config?.hideFilters && !props.config?.isThematic
    ? 'static'
    : props.config?.isThematic
      ? 'thematic'
      : 'filter'
})

const selectedAirTimeDate = computed(() => {
  const zonedAirTime = forceDateIntoTimeZone(selectedFilters.value.airTime!)
  return format(zonedAirTime, 'dd.MM.yyyy')
})

// Offset in days from today for the currently selected airTime. Note this is intentionally not forced to 0 while
// 'now' is active - 'now' can stay selected while browsing a different day (see `selectDay`), so the day chips
// need to reflect the actual day the current airTime falls on.
const activeDayOffset = computed(() => {
  if (!selectedFilters.value.airTime) return 0

  const selected = startOfDay(toZonedTime(new Date(selectedFilters.value.airTime), timeZone))
  const today = startOfDay(toZonedTime(new Date(), timeZone))
  return differenceInCalendarDays(selected, today)
})

// Offset in days from today for the day the filter started out with (used to detect a day-tag-worthy change)
const initialDayOffset = computed(() => {
  const initialAirTime = props.initialFilters?.airTime || airTimeNow.value
  const initialDay = startOfDay(toZonedTime(new Date(initialAirTime), timeZone))
  const today = startOfDay(toZonedTime(new Date(), timeZone))
  return differenceInCalendarDays(initialDay, today)
})

// Time-of-day ("HH:mm") the filter started out with, null if it started out as 'now' (used to detect a time-tag-worthy change)
const initialTimeLabel = computed(() => {
  if (!props.initialFilters?.airTime) return null
  const zoned = toZonedTime(new Date(props.initialFilters.airTime), timeZone)
  return `${String(zoned.getHours()).padStart(2, '0')}:${String(zoned.getMinutes()).padStart(2, '0')}`
})

// Whether the filter started out on the open-ended 'now' rather than a concrete time
const initialIsNow = computed(() => {
  if (props.initialFilters?.airTimeOptions) return props.initialFilters.airTimeOptions === 'now'
  return !props.initialFilters?.airTime
})

// Whether the selected time differs from the one the filter started out with ('now' compared as its own token)
const timeDeviatesFromInitial = computed(() => {
  if (!selectedFilters.value.airTime) return false
  const zoned = toZonedTime(new Date(selectedFilters.value.airTime), timeZone)
  const currentToken = selectedFilters.value.airTimeOptions === 'now'
    ? 'now'
    : `${String(zoned.getHours()).padStart(2, '0')}:${String(zoned.getMinutes()).padStart(2, '0')}`
  const initialToken = initialIsNow.value ? 'now' : initialTimeLabel.value
  return currentToken !== initialToken
})

// 15 day chips: yesterday (-1) through +14 days from today
const dayChips = computed(() => {
  const todayStart = startOfDay(toZonedTime(new Date(), timeZone))
  return Array.from({ length: 15 }, (_, i) => i - 1).map(offset => {
    const day = addDays(todayStart, offset)
    const label = DAY_LABELS[offset] !== undefined ? DAY_LABELS[offset] : WEEKDAY_SHORT[day.getDay()]
    return {
      offset,
      label,
      dateLabel: format(day, 'dd.MM'),
      isActive: activeDayOffset.value === offset
    }
  })
})

// Topic-page day chips: same chip shape/labels as `dayChips`, but bounded to the range the thematic view has
// always allowed (-4..+7 days relative to the day the widget started out on, not to "today" itself) - this
// keeps the existing filter logic/config untouched, only the UI (arrows -> swipe chips) changes.
const thematicDayChips = computed(() => {
  const todayStart = startOfDay(toZonedTime(new Date(), timeZone))
  return Array.from({ length: 12 }, (_, i) => i - 4 + initialDayOffset.value).map(offset => {
    const day = addDays(todayStart, offset)
    const label = DAY_LABELS[offset] !== undefined ? DAY_LABELS[offset] : WEEKDAY_SHORT[day.getDay()]
    return {
      offset,
      label,
      dateLabel: format(day, 'dd.MM'),
      isActive: activeDayOffset.value === offset
    }
  })
})

const timeOptions = computed(() => {
  // Once a custom time is actually selected, show it on the chip itself instead of the generic label
  let customLabel = 'Andere Uhrzeit'
  if (selectedFilters.value.airTimeOptions === 'custom' && selectedFilters.value.airTime) {
    const zoned = toZonedTime(new Date(selectedFilters.value.airTime), timeZone)
    const timeLabel = `${String(zoned.getHours()).padStart(2, '0')}:${String(zoned.getMinutes()).padStart(2, '0')}`
    customLabel = `Andere Uhrzeit (${timeLabel})`
  }

  return [
    { label: 'Jetzt', value: 'now' },
    ...TIME_CHIPS.map(t => ({ label: t, value: t })),
    { label: customLabel, value: 'custom' }
  ]
})

const categoryOptions = computed(() => {
  const categories = (configStore.rsConfig.epg?.broadcastCategories || []).map(item => {
    const key = Object.keys(item)[0]
    return { value: key, label: item[Number(key)] }
  })
  return [{ value: 'all', label: 'Alle' }, ...categories]
})

// Whether the filters were changed since the panel was opened (used to enable the apply button)
const hasPendingChanges = computed(() => {
  if (!filtersOpen.value || filtersMemory.value === null) return false
  return JSON.stringify(selectedFilters.value) !== filtersMemory.value
})

// Tags shown as "Gefiltert nach: ..." while the panel is closed, one per non-default filter
const activeFilterTags = computed(() => {
  const tags: FilterTag[] = []

  // Day tag: only shown once the day deviates from the day the filter started out with
  if (activeDayOffset.value !== initialDayOffset.value && selectedFilters.value.airTime) {
    const dayStart = startOfDay(toZonedTime(new Date(selectedFilters.value.airTime), timeZone))
    const dayLabel = DAY_LABELS[activeDayOffset.value] !== undefined ? DAY_LABELS[activeDayOffset.value] : WEEKDAY_LONG[dayStart.getDay()]
    tags.push({ key: 'day', label: `${dayLabel}, ${format(dayStart, 'dd.MM')}` })
  }

  // Time tag: only for a real deviation that isn't already represented by one of the always-visible quick links
  if (timeDeviatesFromInitial.value && !QUICK_TIME_OPTIONS.includes(selectedFilters.value.airTimeOptions!)) {
    const zoned = toZonedTime(new Date(selectedFilters.value.airTime!), timeZone)
    const timeLabel = `${String(zoned.getHours()).padStart(2, '0')}:${String(zoned.getMinutes()).padStart(2, '0')}`
    tags.push({ key: 'time', label: timeLabel })
  }

  selectedFilters.value.categoryIds.forEach(categoryId => {
    const option = categoryOptions.value.find(opt => opt.value === categoryId)
    if (option) tags.push({ key: 'category', value: categoryId, label: option.label })
  })

  if (selectedFilters.value.freeTvOnly) {
    tags.push({ key: 'freeTvOnly', label: 'Nur Free TV' })
  }

  return tags
})

// Badge count: every deviation from the initial filter, including quick-link times that don't get their own tag
const filterCount = computed(() => {
  const timeSuppressedFromTags = timeDeviatesFromInitial.value && QUICK_TIME_OPTIONS.includes(selectedFilters.value.airTimeOptions!)
  return activeFilterTags.value.length + (timeSuppressedFromTags ? 1 : 0)
})

// Desktop keeps the panel open after applying filters; only the explicit filter toggle closes it there
function isIsoUtcString (value: string) {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
}

// Derive a time chip value from an airTime ISO string
function deriveTimeOption (isoUtcString: string) {
  const zoned = toZonedTime(new Date(isoUtcString), timeZone)
  const timeStr = `${String(zoned.getHours()).padStart(2, '0')}:${String(zoned.getMinutes()).padStart(2, '0')}`
  return TIME_CHIPS.includes(timeStr) ? timeStr : 'custom'
}

// Used by ThematicHeader for day navigation
function updateAirTimeFilter (payload: string) {
  if (isIsoUtcString(payload)) {
    selectedFilters.value.airTime = payload
    selectedFilters.value.airTimeOptions = deriveTimeOption(payload)
  } else {
    selectedFilters.value.airTimeOptions = payload
    selectedFilters.value.airTime = getAirTimeByAirTimeOption(payload, selectedFilters.value.airTime) ?? undefined
  }
  nextTick(() => applyFilters())
}

// Day chip click: keep current time, change the date. If 'now' is active, it stays active - the 'Jetzt' chip
// then means "whatever time it is right now", applied to the selected day (fetchBroadcasts pins this down
// explicitly for the backend once the day isn't today anymore).
function selectDay (offset: number) {
  const todayStart = startOfDay(toZonedTime(new Date(), timeZone))
  const targetDay = addDays(todayStart, offset)

  if (selectedFilters.value.airTimeOptions === 'now') {
    const nowZoned = toZonedTime(new Date(), timeZone)
    targetDay.setHours(nowZoned.getHours(), nowZoned.getMinutes(), 0, 0)
  } else {
    const currentZoned = toZonedTime(new Date(selectedFilters.value.airTime!), timeZone)
    targetDay.setHours(currentZoned.getHours(), currentZoned.getMinutes(), 0, 0)
  }

  selectedFilters.value.airTime = fromZonedTime(targetDay, timeZone).toISOString()
}

// Thematic header has no staging panel/"Anwenden" button (unlike the full filter, where day chip changes are
// staged until applied) - a day chip click must apply/refetch immediately, same as the old prev/next arrows
// did via `updateAirTimeFilter`.
function selectThematicDay (offset: number) {
  selectDay(offset)
  nextTick(() => applyFilters())
}

// Time chip click: keep current day, change the time.
// The compact quicktimes trigger (visible only while the panel is closed) applies instantly,
// while the chips inside the open filter panel just stage the change until the user applies it.
function selectTime (timeOption: string) {
  if (timeOption === 'now') {
    selectedFilters.value.airTimeOptions = 'now'
    selectedFilters.value.airTime = airTimeNow.value
  } else if (timeOption === 'custom') {
    selectedFilters.value.airTimeOptions = 'custom'
    // airTime stays as-is; user will adjust via spinner
  } else {
    const [hours, minutes] = timeOption.split(':').map(Number)
    // Keep the currently selected day (whatever it is) - don't derive it from 'now', since 'now' can itself
    // already be pinned to a day other than today (see `selectDay`)
    const baseZoned = selectedFilters.value.airTime
      ? toZonedTime(new Date(selectedFilters.value.airTime), timeZone)
      : toZonedTime(new Date(), timeZone)

    const targetDay = startOfDay(baseZoned)
    targetDay.setHours(hours, minutes, 0, 0)
    selectedFilters.value.airTime = fromZonedTime(targetDay, timeZone).toISOString()
    selectedFilters.value.airTimeOptions = timeOption
  }

  if (!filtersOpen.value) applyFilters()
}

// Custom time spinner change: keep current day, set specific HH:MM
function updateCustomTime ({ hours, minutes }: { hours: string, minutes: string }) {
  const baseZoned = selectedFilters.value.airTime
    ? toZonedTime(new Date(selectedFilters.value.airTime), timeZone)
    : toZonedTime(new Date(), timeZone)

  const targetDay = startOfDay(baseZoned)
  targetDay.setHours(parseInt(hours), parseInt(minutes), 0, 0)
  selectedFilters.value.airTime = fromZonedTime(targetDay, timeZone).toISOString()
  selectedFilters.value.airTimeOptions = 'custom'
}

// Category chip click: toggle or set 'all'
function selectCategory (value: string) {
  if (value === 'all') {
    selectedFilters.value.categoryIds = []
    selectedFilters.value.categoryAll = true
  } else {
    const idx = selectedFilters.value.categoryIds.indexOf(value)
    selectedFilters.value.categoryIds = idx >= 0
      ? selectedFilters.value.categoryIds.filter(id => id !== value)
      : [...selectedFilters.value.categoryIds, value]
    selectedFilters.value.categoryAll = selectedFilters.value.categoryIds.length === 0
  }
}

function updateFreeTvOnly (value: boolean) {
  selectedFilters.value.freeTvOnly = value
}

function handleFilterToggle () {
  if (!filtersOpen.value) {
    filtersMemory.value = JSON.stringify(selectedFilters.value)
    trackEventInDataLayer('epg_filter_open_click')
  } else {
    const filtersString = JSON.stringify(selectedFilters.value)
    if (filtersMemory.value !== filtersString) {
      showApplyOrDiscardDialog.value = true
      return
    }
  }
  filtersOpen.value = !filtersOpen.value
}

function handleFilterToggleDialogEmit (action: 'apply' | 'discard' | 'change') {
  if (action === 'apply') {
    applyFilters()
    // This dialog only appears as a result of the explicit filter toggle click, so always close here
    filtersOpen.value = false
  } else if (action === 'discard') {
    selectedFilters.value = JSON.parse(filtersMemory.value!)
    filtersOpen.value = false
  }
  showApplyOrDiscardDialog.value = false
}

function handleResetFilters () {
  resetFilters()
  // Mobile keeps the panel open so the user can re-select from the initial state and apply explicitly (PO wish);
  // desktop stays live and updates results right away
  if (isDesktopViewport()) {
    applyFilters()
  }
}

function resetFilters () {
  selectedFilters.value = {
    airTime: airTimeNow.value,
    airTimeOptions: 'now',
    categoryAll: true,
    genreIds: [],
    phrase: '',
    packageName: 'all',
    isDailyTip: false,
    isLive: false,
    isNew: false,
    payTvOnly: false,
    freeTvOnly: false,
    offset: 0,
    limit: 10,
    ...props.initialFilters,
    categoryIds: props.initialFilters?.categoryIds?.map(String) ?? []
  }

  if (props.initialFilters?.airTime && !props.initialFilters.airTimeOptions) {
    selectedFilters.value.airTimeOptions = deriveTimeOption(props.initialFilters.airTime)
  }
}

function applyFilters () {
  emit('update-filters', selectedFilters.value)
  // Keep the memory in sync so a later close doesn't think the just-applied filters are still pending
  filtersMemory.value = JSON.stringify(selectedFilters.value)
  if (!isDesktopViewport()) {
    filtersOpen.value = false
  }
}

// Removes a single active filter tag (shown while the panel is closed) and applies instantly
function removeFilterTag (tag: FilterTag) {
  // The time-of-day the filter started out with, falling back to the same default used when leaving 'now'
  const initialZoned = props.initialFilters?.airTime
    ? toZonedTime(new Date(props.initialFilters.airTime), timeZone)
    : null
  const initialHours = initialZoned ? initialZoned.getHours() : 20
  const initialMinutes = initialZoned ? initialZoned.getMinutes() : 15

  if (tag.key === 'day') {
    // Reset the day back to where the filter started out - same logic as a regular day chip click
    selectDay(initialDayOffset.value)
  } else if (tag.key === 'time') {
    // Reset the time back to where the filter started out, keep the currently selected day
    const targetDay = startOfDay(toZonedTime(new Date(selectedFilters.value.airTime!), timeZone))
    targetDay.setHours(initialHours, initialMinutes, 0, 0)
    selectedFilters.value.airTime = fromZonedTime(targetDay, timeZone).toISOString()
    selectedFilters.value.airTimeOptions = deriveTimeOption(selectedFilters.value.airTime)
  } else if (tag.key === 'category') {
    selectedFilters.value.categoryIds = selectedFilters.value.categoryIds.filter(id => id !== tag.value)
    selectedFilters.value.categoryAll = selectedFilters.value.categoryIds.length === 0
  } else if (tag.key === 'freeTvOnly') {
    selectedFilters.value.freeTvOnly = false
  }
  applyFilters()
}

useMobileDialogBodyClass(filtersOpen, { scrollToTop: true })

watch(() => props.initialFilters, () => {
  if (pageStore.isPreview && filtersReady.value) {
    resetFilters()
    applyFilters()
  }
}, { deep: true })

// Persisted filters are applied only once the parent restores them after hydration (via the reactive
// `restoredFilters` prop). Applying them during setup would run mid-hydration and diverge from the
// server-rendered initial filters. `resetFilters()` stays the page-default baseline for the explicit reset.
watch(() => props.restoredFilters, (restored) => {
  if (!restored) return
  selectedFilters.value = { ...selectedFilters.value, ...restored }
  applyFilters()
})

resetFilters()
emit('filters-ready', selectedFilters.value)
filtersReady.value = true

onMounted(() => {
  // Only the 'filter' variant has a collapsible panel to begin with - channel/thematic/static headers have
  // nothing to expand, so forcing `filtersOpen` there just attaches `.bx-js-filters-open`'s layout for nothing
  if (activeHeaderVariant.value === 'filter' && isDesktopViewport() && !props.isPreview) {
    filtersOpen.value = true
    filtersMemory.value = JSON.stringify(selectedFilters.value)
  }
})
</script>

<template>
  <AirTimeDateHeading v-if="activeHeaderVariant === 'filter'"
                      :selected-air-time-date="selectedAirTimeDate" />

  <form class="bx-form bx-typo--form bx-form--search bx-form--search-epg bx-typo--form-search"
        :class="{ 'bx-js-filters-open': filtersOpen }">
    <ThematicHeader v-if="activeHeaderVariant === 'thematic'"
                    :headline="!config?.hideHeadline ? config?.headline : ''"
                    :day-chips="thematicDayChips"
                    @select-day="selectThematicDay" />
    <ChannelHeader v-if="activeHeaderVariant === 'channel'"
                   :selected-air-time="selectedFilters.airTime"
                   :initial-air-time="initialFilters?.airTime"
                   :channels="channels"
                   :is-preview="isPreview"
                   @update:air-time-filter="updateAirTimeFilter" />
    <StaticHeader v-if="activeHeaderVariant === 'static'"
                  :headline="!config?.hideHeadline ? config?.headline : ''"
                  :short-link="config?.shortLink" />

    <AirTimeDateHeading v-if="activeHeaderVariant === 'static'"
                        :selected-air-time-date="selectedAirTimeDate" />

    <FilterHeader v-if="activeHeaderVariant === 'filter'"
                  :filters-open="filtersOpen"
                  :selected-filters="selectedFilters"
                  :day-chips="dayChips"
                  :time-options="timeOptions"
                  :category-options="categoryOptions"
                  :apply-disabled="!hasPendingChanges"
                  :filter-count="filterCount"
                  @toggle-filters="handleFilterToggle"
                  @select-day="selectDay"
                  @select-time="selectTime"
                  @update-custom-time="updateCustomTime"
                  @select-category="selectCategory"
                  @update:free-tv-only="updateFreeTvOnly"
                  @reset-filters="handleResetFilters"
                  @apply-filters="applyFilters" />

    <ApplyOrDiscardDialog v-if="showApplyOrDiscardDialog"
                          @apply-filters="handleFilterToggleDialogEmit('apply')"
                          @discard-filters="handleFilterToggleDialogEmit('discard')"
                          @change-filters="handleFilterToggleDialogEmit('change')" />

    <SelectedFilters v-if="activeHeaderVariant === 'filter' && !filtersOpen"
                     :tags="activeFilterTags"
                     @remove-filter="removeFilterTag" />
  </form>
</template>
